'use client';
import React, { useEffect, useRef } from 'react';

function StockChartAnimation() {
  // Path segments: each segment has start point, end point, and direction (up=blue, down=red)
  // We'll use an SVG with multiple animated polyline segments
  // The chart goes: start low → spike up → dip down → rise up → dip down → dramatic spike very high
  const segments = [
    // [x1, y1, x2, y2, color]
    { points: '0,320 60,290 120,260', color: '#3b82f6', delay: 0 },         // rising (blue)
    { points: '120,260 160,300 200,330', color: '#ef4444', delay: 0.4 },     // dip down (red)
    { points: '200,330 260,280 320,230 380,200', color: '#3b82f6', delay: 0.8 }, // rising (blue)
    { points: '380,200 420,240 460,270', color: '#ef4444', delay: 1.3 },     // dip down (red)
    { points: '460,270 500,230 540,180 580,120 620,50 660,10', color: '#3b82f6', delay: 1.7 }, // dramatic spike (blue)
  ];

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
      <svg
        viewBox="0 0 700 380"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.18 }}
      >
        <defs>
          {segments?.map((seg, i) => (
            <filter key={i} id={`glow-${i}`}>
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>

        {/* Subtle grid lines */}
        {[80, 160, 240, 320]?.map((y) => (
          <line key={y} x1="0" y1={y} x2="700" y2={y} stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.08" />
        ))}
        {[140, 280, 420, 560]?.map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="380" stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.08" />
        ))}

        {/* Animated chart segments */}
        {segments?.map((seg, i) => (
          <polyline
            key={i}
            points={seg?.points}
            fill="none"
            stroke={seg?.color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#glow-${i})`}
            style={{
              strokeDasharray: 1000,
              strokeDashoffset: 1000,
              animation: `drawLine 0.8s ease-out ${seg?.delay + 0.5}s forwards`,
            }}
          />
        ))}

        {/* Glowing dot at the tip of the final spike */}
        <circle
          cx="660"
          cy="10"
          r="6"
          fill="#3b82f6"
          style={{
            opacity: 0,
            animation: 'fadeInDot 0.4s ease-out 3.2s forwards',
            filter: 'drop-shadow(0 0 8px #3b82f6)',
          }}
        />
        <circle
          cx="660"
          cy="10"
          r="12"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="1.5"
          style={{
            opacity: 0,
            animation: 'fadeInDot 0.4s ease-out 3.2s forwards',
          }}
        />
      </svg>
      <style>{`
        @keyframes drawLine {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes fadeInDot {
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef?.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #080808 0%, #0f0a00 40%, #0a0800 70%, #080808 100%)' }}
    >
      {/* Background grid */}
      <div className="absolute inset-0 grid-pattern opacity-40"></div>

      {/* Stock market animation */}
      <StockChartAnimation />

      {/* Orbs */}
      <div className="orb orb-gold w-96 h-96 top-1/4 right-1/4 opacity-25 animate-pulse-slow"></div>
      <div className="orb orb-dark-gold w-64 h-64 bottom-1/3 left-1/4 opacity-20 animate-float-delay"></div>
      <div className="orb orb-gold w-80 h-80 top-1/2 left-1/3 opacity-15 animate-float-delay-2"></div>
      {/* Floating shapes */}
      <div className="absolute top-20 right-10 w-20 h-20 border border-amber-500/20 rounded-2xl rotate-12 animate-float opacity-40"></div>
      <div className="absolute bottom-32 left-10 w-16 h-16 border border-amber-400/20 rounded-full animate-float-delay opacity-40"></div>
      <div className="absolute top-1/3 left-10 w-12 h-12 bg-amber-600/10 rounded-xl rotate-45 animate-float-delay-2"></div>
      <div className="absolute bottom-20 right-20 w-24 h-24 border border-amber-400/10 rounded-3xl -rotate-12 animate-float"></div>
      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="text-center">
          {/* Main heading */}
          <h1 className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6">
            <span className="text-white">نحوّل أعمالك إلى</span>
            <br />
            <span className="gradient-text">إمبراطورية رقمية</span>
          </h1>

          {/* Subtitle */}
          <p className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200 text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            ننمي أعمالك ونجذب لك عملاء جدد باستمرار. ندمج التسويق الذكي، تصميم المواقع عالية التحويل، والأتمتة لتوليد عملاء محتملين وزيادة مبيعاتك دون هدر في الميزانية.
          </p>

          {/* CTA Buttons */}
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-300 flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href="#contact"
              onClick={(e) => { e?.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-primary px-8 py-4 rounded-2xl font-bold text-lg w-full sm:w-auto text-center"
            >
              طلب استشارة مجانية لبناء نظام مبيعاتك ←
            </a>
            <a
              href="#services"
              onClick={(e) => { e?.preventDefault(); document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-outline px-8 py-4 rounded-2xl font-bold text-lg w-full sm:w-auto text-center"
            >
              استكشف خدماتنا
            </a>
          </div>

          {/* Stats row */}
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-500 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { number: '24/7', label: 'دعم فني مستمر' },
              { number: '100%', label: 'التزام بالتسليم' },
              { number: '1-on-1', label: 'استشارة مباشرة' },
              { number: '98%', label: 'تركيز على النمو' },
            ]?.map((stat, i) => (
              <div
                key={i}
                className="stat-card rounded-2xl p-4 text-center"
              >
                <div className="text-3xl font-black gradient-text mb-1">{stat?.number}</div>
                <div className="text-gray-400 text-sm font-medium">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
